// The sky lab's stand-in world (`?scene=sky`, gate C3): until the world stream's valley lands, a small square of
// rolling ground with four places to frame the six place-and-time shots: an open meadow, a forest with a clearing
// (where the light shafts stand), a town of plastered houses with windows and lanterns that glow at night, ruins of
// broken walls, arches and glowing crystals, and a cottage with a furnished room (the interior shot). Everything is
// faceted on the look's one surface material and merged into one static mesh; the town's lantern heads are their own
// mesh (the planted late-shader defect swaps their material at dusk). Stand-ins only: the integrator re-takes the
// shots in the real valley. Same seed, same lab.
import * as THREE from "three";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { ShaftSpot } from "@rpg/look";

/** The lab's half-size (m). */
export const LAB_HALF = 70;

/** The places' centres. */
export const PLACES = {
  meadow: { x: 0, z: 18 },
  forest: { x: -36, z: -12 },
  town: { x: 34, z: -8 },
  ruins: { x: 2, z: -46 },
  cottage: { x: 46, z: 34 },
} as const;

/** The ground's height: low rolling hills, flattened under the town, the ruins and the cottage. */
export function skyLabHeight(x: number, z: number): number {
  const h = 0.9 * Math.sin(x * 0.06 + 0.4) * Math.cos(z * 0.055) + 0.35 * Math.sin(x * 0.13 + z * 0.11) + 0.012 * Math.max(0, -z - 20) * 2;
  let flat = 1;
  for (const p of [PLACES.town, PLACES.ruins, PLACES.cottage]) flat = Math.min(flat, Math.min(1, Math.max(0, (Math.hypot(x - p.x, z - p.z) - 12) / 8)));
  const base = 0.4;
  return base + (h - base) * flat;
}

/** The lab's built geometry and its marks. */
export interface StandIns {
  /** Every static thing, merged (position, normal, colour, glow). */
  world: THREE.BufferGeometry;
  /** The town's lantern heads (glow 2: lamps), their own mesh. */
  lanterns: THREE.BufferGeometry;
  /** Where the light shafts stand (the forest's clearing and gaps). */
  shafts: ShaftSpot[];
}

type Pal = (key: string) => THREE.Color;

/** A gable roof: a triangular prism along z, its base `w` wide (plus a 0.3 m overhang each side) at y = 0, apex up,
 *  `pitch` the height over the half-width. */
function prism(w: number, len: number, pitch: number): THREE.BufferGeometry {
  const half = w / 2 + 0.3;
  const r = half / Math.sin(Math.PI / 3);
  const g = new THREE.CylinderGeometry(r, r, len, 3);
  g.rotateX(-Math.PI / 2); // apex at +y, the base corners at y = −r/2
  g.translate(0, r / 2, 0);
  g.scale(1, (pitch * half) / (1.5 * r), 1);
  return g;
}

/** Build the stand-ins. `pal` is the look's palette lookup (keys or hex). */
export function buildStandIns(pal: Pal): StandIns {
  const rng = seeded(0x5ca1ab);
  const parts: THREE.BufferGeometry[] = [];
  const lanterns: THREE.BufferGeometry[] = [];
  const put = (g: THREE.BufferGeometry, colour: string, o: { pos: [number, number, number]; rot?: [number, number, number]; scale?: [number, number, number]; jitter?: number; tint?: number; glow?: number; into?: THREE.BufferGeometry[] }): void => {
    const m = new THREE.Matrix4().compose(new THREE.Vector3(...o.pos), new THREE.Quaternion().setFromEuler(new THREE.Euler(...(o.rot ?? [0, 0, 0]))), new THREE.Vector3(...(o.scale ?? [1, 1, 1])));
    g.applyMatrix4(m);
    (o.into ?? parts).push(facet(g, { colour: pal(colour), jitter: o.jitter ?? 0, tint: o.tint ?? 0.06, glow: o.glow ?? 0, rng }));
  };
  const y = skyLabHeight;

  // ── the ground: a grid of facets coloured by place ──
  const N = 70;
  const S = LAB_HALF * 2;
  const pos: number[] = [];
  const col: number[] = [];
  const grass = ["grass.a", "grass.b", "grass.c", "grass.d"].map(pal);
  const forestFloor = [pal("#5f9448"), pal("#568a42"), pal("#64994c")];
  const path = ["path.a", "path.b"].map(pal);
  const stone = ["stone.a", "stone.b"].map(pal);
  const near = (x: number, z: number, p: { x: number; z: number }, r: number) => Math.hypot(x - p.x, z - p.z) < r;
  const colourAt = (x: number, z: number): THREE.Color => {
    const pick = (a: THREE.Color[]) => a[Math.floor(rng() * a.length)]!;
    if (near(x, z, PLACES.town, 14) && (Math.abs(z - PLACES.town.z) < 2.6 || Math.abs(x - PLACES.town.x) < 2.6)) return pick(path);
    if (near(x, z, PLACES.ruins, 13) && rng() < 0.45) return pick(stone);
    if (near(x, z, PLACES.forest, 20)) return pick(forestFloor);
    // the path from the meadow to the town
    if (Math.abs(z - (PLACES.meadow.z + (x - PLACES.meadow.x) * ((PLACES.town.z - PLACES.meadow.z) / (PLACES.town.x - PLACES.meadow.x)))) < 1.6 && x > PLACES.meadow.x && x < PLACES.town.x) return pick(path);
    return pick(grass);
  };
  const v = (i: number, j: number): [number, number, number] => {
    const x = -LAB_HALF + (i / N) * S;
    const z = -LAB_HALF + (j / N) * S;
    return [x, y(x, z), z];
  };
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i, j + 1);
      const d = v(i + 1, j + 1);
      for (const tri of [
        [a, c, b],
        [b, c, d],
      ]) {
        const cx = (tri[0]![0] + tri[1]![0] + tri[2]![0]) / 3;
        const cz = (tri[0]![2] + tri[1]![2] + tri[2]![2]) / 3;
        const k = colourAt(cx, cz).clone().multiplyScalar(1 + (rng() * 2 - 1) * 0.04);
        for (const p of tri) {
          pos.push(...p);
          col.push(k.r, k.g, k.b);
        }
      }
    }
  const ground = new THREE.BufferGeometry();
  ground.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  ground.computeVertexNormals();
  ground.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  ground.setAttribute("glow", new THREE.Float32BufferAttribute(new Float32Array(pos.length / 3), 1));
  parts.push(ground);

  // ── trees ──
  const tree = (x: number, z: number, s: number, pine: boolean): void => {
    const gy = y(x, z);
    put(new THREE.CylinderGeometry(0.16 * s, 0.26 * s, 3 * s, 6), "trunk", { pos: [x, gy + 1.5 * s, z], jitter: 0.03 });
    const leaf = pine ? (rng() < 0.5 ? "pine.a" : "pine.b") : ["leaf.a", "leaf.b", "leaf.c"][Math.floor(rng() * 3)]!;
    if (pine) {
      put(new THREE.ConeGeometry(1.5 * s, 2.8 * s, 7), leaf, { pos: [x, gy + 3.2 * s, z], jitter: 0.08 * s });
      put(new THREE.ConeGeometry(1.1 * s, 2.4 * s, 7), leaf, { pos: [x, gy + 4.6 * s, z], jitter: 0.06 * s });
    } else {
      put(new THREE.IcosahedronGeometry(1.5 * s, 0), leaf, { pos: [x, gy + 4.1 * s, z], jitter: 0.18 * s, scale: [1, 0.85, 1] });
      put(new THREE.IcosahedronGeometry(1.0 * s, 0), leaf, { pos: [x + 0.7 * s, gy + 3.5 * s, z - 0.3 * s], jitter: 0.12 * s });
    }
  };
  // the forest: a ring of trees round a clearing, thicker outside it
  const shafts: ShaftSpot[] = [];
  const F = PLACES.forest;
  for (let k = 0; k < 120; k++) {
    const a = rng() * Math.PI * 2;
    const r = 8.5 + rng() * 17;
    const x = F.x + Math.cos(a) * r;
    const z = F.z + Math.sin(a) * r;
    // a gap where the forest shot's camera stands, looking into the clearing
    if (Math.hypot(x - (F.x + 8), z - (F.z + 6)) < 4.5) continue;
    tree(x, z, 0.9 + rng() * 0.7, rng() < 0.4);
  }
  for (let k = 0; k < 7; k++) {
    const a = (k / 7) * Math.PI * 2 + rng() * 0.4;
    const r = k === 0 ? 0 : 2 + rng() * 6;
    const x = F.x + Math.cos(a) * r;
    const z = F.z + Math.sin(a) * r;
    shafts.push({ x, y: y(x, z) - 0.2, z, length: 16 + rng() * 6, width: 1.6 + rng() * 1.6 });
  }
  // a few trees round the meadow and the town, and along the lab's edge
  for (let k = 0; k < 26; k++) {
    const a = rng() * Math.PI * 2;
    const r = 50 + rng() * 16;
    tree(Math.cos(a) * r, Math.sin(a) * r, 1 + rng() * 0.5, rng() < 0.5);
  }
  for (const [x, z] of [
    [-12, 8],
    [-9, 26],
    [14, 30],
    [20, 6],
  ] as const)
    tree(x, z, 1.1, false);

  // ── the meadow: rocks and flowers ──
  const M = PLACES.meadow;
  for (let k = 0; k < 14; k++) {
    const x = M.x + (rng() * 2 - 1) * 16;
    const z = M.z + (rng() * 2 - 1) * 14;
    put(new THREE.DodecahedronGeometry(0.35 + rng() * 0.5, 0), rng() < 0.5 ? "stone.a" : "stone.b", { pos: [x, y(x, z) + 0.15, z], jitter: 0.08 });
  }
  const flowers = ["fruit.red", "fruit.yellow", "#f2f0ff", "#c9a0ff"];
  for (let k = 0; k < 90; k++) {
    const x = M.x + (rng() * 2 - 1) * 18;
    const z = M.z + (rng() * 2 - 1) * 16;
    put(new THREE.OctahedronGeometry(0.12, 0), flowers[k % flowers.length]!, { pos: [x, y(x, z) + 0.22, z], tint: 0.1 });
    put(new THREE.CylinderGeometry(0.015, 0.015, 0.22, 3), "grass.b", { pos: [x, y(x, z) + 0.1, z], tint: 0 });
  }

  // ── the town: plastered houses with roofs, doors, windows (lamps) and lanterns ──
  const T = PLACES.town;
  const house = (x: number, z: number, yaw: number, w: number, d: number, h: number, roof: string): void => {
    const gy = y(x, z);
    const R: [number, number, number] = [0, yaw, 0];
    const o = new THREE.Vector3(x, gy, z);
    const at = (lx: number, ly: number, lz: number): [number, number, number] => {
      const p = new THREE.Vector3(lx, ly, lz).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).add(o);
      return [p.x, p.y, p.z];
    };
    put(new THREE.BoxGeometry(w, h, d), "#ead9b8", { pos: at(0, h / 2, 0), rot: R, tint: 0.03 });
    put(new THREE.BoxGeometry(w + 0.3, 0.35, d + 0.3), "#8a5a34", { pos: at(0, 0.17, 0), rot: R });
    // timber bands
    put(new THREE.BoxGeometry(w + 0.06, 0.18, d + 0.06), "#6a4428", { pos: at(0, h * 0.55, 0), rot: R });
    // the roof: a triangular prism along the house's length, apex up
    put(prism(w, d + 0.7, 0.75), roof, { pos: at(0, h, 0), rot: R, jitter: 0.03 });
    // the door and the windows (glow 2: lamps that light at night)
    put(new THREE.BoxGeometry(0.9, 1.7, 0.08), "#4a2e1c", { pos: at(0, 0.85 + 0.35, d / 2 + 0.03), rot: R });
    for (const wx of [-w / 2 + 0.75, w / 2 - 0.75]) {
      put(new THREE.BoxGeometry(0.6, 0.7, 0.08), "#ffcf7a", { pos: at(wx, h * 0.72, d / 2 + 0.03), rot: R, glow: 2, tint: 0 });
      put(new THREE.BoxGeometry(0.6, 0.7, 0.08), "#ffcf7a", { pos: at(wx, h * 0.72, -d / 2 - 0.03), rot: R, glow: 2, tint: 0 });
    }
    put(new THREE.BoxGeometry(0.08, 0.7, 0.6), "#ffcf7a", { pos: at(w / 2 + 0.03, h * 0.72, 0), rot: R, glow: 2, tint: 0 });
  };
  const roofs = ["#a2452e", "#8a3a2a", "#6d6f7a", "#a8582e"];
  const homes: [number, number, number, number, number, number][] = [
    [T.x - 7, T.z - 6, 0, 4.4, 5.2, 3.2],
    [T.x + 1, T.z - 7, 0, 5, 5.6, 3.6],
    [T.x + 8.5, T.z - 6, 0, 4.2, 5, 3],
    [T.x - 7, T.z + 6, Math.PI, 4.6, 5.4, 3.4],
    [T.x + 1.5, T.z + 7, Math.PI, 4.8, 5.4, 3.2],
    [T.x + 9, T.z + 6.5, Math.PI, 4, 4.8, 3],
  ];
  homes.forEach((h, i) => house(h[0], h[1], h[2], h[3], h[4], h[5], roofs[i % roofs.length]!));
  // lanterns along the street
  for (const [x, z] of [
    [T.x - 11, T.z - 2.8],
    [T.x - 3, T.z + 2.8],
    [T.x + 5, T.z - 2.8],
    [T.x + 12, T.z + 2.8],
  ] as const) {
    const gy = y(x, z);
    put(new THREE.BoxGeometry(0.12, 2.4, 0.12), "#3a2a20", { pos: [x, gy + 1.2, z] });
    put(new THREE.BoxGeometry(0.34, 0.42, 0.34), "#ffd27a", { pos: [x, gy + 2.55, z], glow: 2, tint: 0, into: lanterns });
    put(new THREE.ConeGeometry(0.3, 0.22, 4), "#3a2a20", { pos: [x, gy + 2.86, z], rot: [0, Math.PI / 4, 0] });
  }
  // the well
  {
    const gy = y(T.x - 1.5, T.z);
    put(new THREE.CylinderGeometry(0.9, 1, 0.8, 10, 1, true), "stone.a", { pos: [T.x - 1.5, gy + 0.4, T.z + 0.2], jitter: 0.03 });
    put(new THREE.BoxGeometry(0.12, 1.6, 0.12), "wood", { pos: [T.x - 2.35, gy + 1.2, T.z + 0.2] });
    put(new THREE.BoxGeometry(0.12, 1.6, 0.12), "wood", { pos: [T.x - 0.65, gy + 1.2, T.z + 0.2] });
    put(new THREE.ConeGeometry(1.3, 0.7, 4), "#8a3a2a", { pos: [T.x - 1.5, gy + 2.2, T.z + 0.2], rot: [0, Math.PI / 4, 0] });
  }

  // ── the ruins: broken walls, arches, column stumps, glowing crystals ──
  const Rn = PLACES.ruins;
  const wall = (x0: number, z0: number, x1: number, z1: number, hMax: number): void => {
    const len = Math.hypot(x1 - x0, z1 - z0);
    const segs = Math.max(1, Math.round(len / 1.2));
    const yaw = Math.atan2(x1 - x0, z1 - z0);
    for (let s = 0; s < segs; s++) {
      const t = (s + 0.5) / segs;
      const x = x0 + (x1 - x0) * t;
      const z = z0 + (z1 - z0) * t;
      const h = 0.5 + rng() * hMax;
      put(new THREE.BoxGeometry(0.7, h, len / segs + 0.02), rng() < 0.5 ? "stone.a" : "stone.b", { pos: [x, y(x, z) + h / 2, z], rot: [0, yaw, 0], jitter: 0.06 });
    }
  };
  wall(Rn.x - 9, Rn.z - 6, Rn.x + 7, Rn.z - 8, 3.5);
  wall(Rn.x - 9, Rn.z - 6, Rn.x - 10, Rn.z + 5, 2.5);
  wall(Rn.x + 7, Rn.z - 8, Rn.x + 9, Rn.z + 2, 3);
  const arch = (x: number, z: number, yaw: number): void => {
    const gy = y(x, z);
    const off = new THREE.Vector3(1.6, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    for (const sgn of [-1, 1]) put(new THREE.BoxGeometry(0.8, 4, 0.8), "stone.a", { pos: [x + off.x * sgn, gy + 2, z + off.z * sgn], rot: [0, yaw, 0], jitter: 0.05 });
    put(new THREE.BoxGeometry(4.2, 0.7, 0.9), "stone.b", { pos: [x, gy + 4.3, z], rot: [0, yaw, 0], jitter: 0.06 });
  };
  arch(Rn.x - 2, Rn.z + 2, 0.15);
  arch(Rn.x + 5, Rn.z + 6, -0.4);
  for (let k = 0; k < 7; k++) {
    const a = (k / 7) * Math.PI * 2;
    const x = Rn.x + Math.cos(a) * 7.5;
    const z = Rn.z + 4 + Math.sin(a) * 6;
    const h = 0.6 + rng() * 2.4;
    put(new THREE.CylinderGeometry(0.42, 0.5, h, 7), "stone.a", { pos: [x, y(x, z) + h / 2, z], jitter: 0.05 });
  }
  for (const [x, z, s] of [
    [Rn.x - 4, Rn.z - 2, 1],
    [Rn.x + 3, Rn.z - 4, 0.8],
    [Rn.x + 1, Rn.z + 9, 1.2],
  ] as const) {
    put(new THREE.OctahedronGeometry(0.45 * s, 0), "#9fd8ff", { pos: [x, y(x, z) + 0.5 * s, z], scale: [0.7, 1.6, 0.7], glow: 1, tint: 0 });
  }

  // ── the cottage: a room with a window and a door, furnished, a lamp on the table ──
  const C = PLACES.cottage;
  {
    const gy = y(C.x, C.z);
    const W = 7;
    const D = 6;
    const H = 3;
    const t = 0.25;
    const wallC = "#e6d4b0";
    put(new THREE.BoxGeometry(W, 0.2, D), "#8a6440", { pos: [C.x, gy + 0.1, C.z], tint: 0.04 });
    // the back wall (−z), the side walls, the front wall with a door gap and a window gap
    put(new THREE.BoxGeometry(W, H, t), wallC, { pos: [C.x, gy + H / 2, C.z - D / 2] });
    put(new THREE.BoxGeometry(t, H, D), wallC, { pos: [C.x - W / 2, gy + H / 2, C.z] });
    // the east wall has a window: below, above, and both sides of it
    put(new THREE.BoxGeometry(t, 1.1, D), wallC, { pos: [C.x + W / 2, gy + 0.55, C.z] });
    put(new THREE.BoxGeometry(t, 0.8, D), wallC, { pos: [C.x + W / 2, gy + H - 0.4, C.z] });
    put(new THREE.BoxGeometry(t, 1.1, 2), wallC, { pos: [C.x + W / 2, gy + 1.65, C.z - 2] });
    put(new THREE.BoxGeometry(t, 1.1, 2), wallC, { pos: [C.x + W / 2, gy + 1.65, C.z + 2] });
    // the front wall (+z) with a door
    put(new THREE.BoxGeometry(2.6, H, t), wallC, { pos: [C.x - W / 2 + 1.3, gy + H / 2, C.z + D / 2] });
    put(new THREE.BoxGeometry(3.2, H, t), wallC, { pos: [C.x + W / 2 - 1.6, gy + H / 2, C.z + D / 2] });
    put(new THREE.BoxGeometry(1.2, 0.9, t), wallC, { pos: [C.x - W / 2 + 3.2, gy + H - 0.45, C.z + D / 2] });
    // beams and the roof
    for (const bx of [-2, 0, 2]) put(new THREE.BoxGeometry(0.2, 0.22, D), "#6a4428", { pos: [C.x + bx, gy + H - 0.11, C.z] });
    put(prism(W, D + 0.8, 0.7), "#8a3a2a", { pos: [C.x, gy + H, C.z], jitter: 0.03 });
    put(new THREE.BoxGeometry(W, 0.12, D), "#7a5636", { pos: [C.x, gy + H + 0.06, C.z] });
    // the table, a stool, a bed, a shelf, a rug; the lamp on the table glows at night
    put(new THREE.BoxGeometry(1.6, 0.1, 0.9), "wood", { pos: [C.x + 1.2, gy + 0.85, C.z - 1.2] });
    for (const [lx, lz] of [
      [0.7, 0.35],
      [-0.7, 0.35],
      [0.7, -0.35],
      [-0.7, -0.35],
    ] as const)
      put(new THREE.BoxGeometry(0.1, 0.8, 0.1), "wood", { pos: [C.x + 1.2 + lx, gy + 0.4 + 0.1, C.z - 1.2 + lz] });
    put(new THREE.CylinderGeometry(0.25, 0.25, 0.5, 6), "wood", { pos: [C.x + 0.2, gy + 0.45, C.z - 0.6] });
    put(new THREE.BoxGeometry(2.1, 0.45, 1.2), "#8a5a34", { pos: [C.x - 2.2, gy + 0.42, C.z - 1.9] });
    put(new THREE.BoxGeometry(2, 0.18, 1.1), "#c9d4e8", { pos: [C.x - 2.2, gy + 0.72, C.z - 1.9] });
    put(new THREE.BoxGeometry(1.4, 0.06, 2.2), "#a8423a", { pos: [C.x - 0.4, gy + 0.23, C.z + 0.8] });
    put(new THREE.BoxGeometry(1.6, 0.08, 0.35), "wood", { pos: [C.x - 1.2, gy + 1.9, C.z - D / 2 + 0.3] });
    for (let k = 0; k < 5; k++) put(new THREE.BoxGeometry(0.18, 0.3, 0.2), ["fruit.red", "fruit.yellow", "basket.weave"][k % 3]!, { pos: [C.x - 1.8 + k * 0.3, gy + 2.09, C.z - D / 2 + 0.3] });
    put(new THREE.BoxGeometry(0.22, 0.3, 0.22), "#ffd27a", { pos: [C.x + 1.5, gy + 1.05, C.z - 1.2], glow: 2, tint: 0 });
    put(new THREE.BoxGeometry(0.6, 0.5, 0.08), "#ffcf7a", { pos: [C.x - 2.4, gy + 1.9, C.z - D / 2 + 0.17], glow: 2, tint: 0 });
  }

  return { world: merge(parts), lanterns: merge(lanterns), shafts };
}
